import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import GoogleAuthButton from "./GoogleAuthButton";
import { LoadingScreen } from "../common/LoadingSpinner";
import ThemeToggle from "../layout/ThemeToggle";
import {
	CalendarDays,
	Sparkles,
	Clock,
	Bot,
	Users,
	Lightbulb,
} from "lucide-react";

import darkLogo from "../../assets/DarkMode.png";
import lightLogo from "../../assets/LightMode.png";
import example1 from "../../assets/Example1.png";
import example2 from "../../assets/Example2.png";

const AnimatedExampleImage = () => {
	const [rotation, setRotation] = useState({ x: 0, y: 0 });
	const [isHovered, setIsHovered] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });

	const handleMouseMove = (e) => {
		if (!isHovered) return;

		const container = e.currentTarget;
		const rect = container.getBoundingClientRect();

		const x = (e.clientX - rect.left) / rect.width;
		const y = (e.clientY - rect.top) / rect.height;

		const rotateX = (0.5 - y) * 10;
		const rotateY = (x - 0.5) * 10;

		const shadowX = (0.5 - x) * 20;
		const shadowY = (0.5 - y) * 20;

		setRotation({ x: rotateX, y: rotateY });
		setPosition({ x: shadowX, y: shadowY });
	};

	const handleMouseLeave = () => {
		setIsHovered(false);
		setRotation({ x: 0, y: 0 });
		setPosition({ x: 0, y: 0 });
	};

	const handleMouseEnter = () => {
		setIsHovered(true);
	};

	return (
		<div
			className="relative w-full group"
			onMouseMove={handleMouseMove}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<div
				className={`
          relative w-full 
          transition-all duration-150 ease-out transform-gpu 
          rounded-lg
          bg-gray-100 dark:bg-gray-800
          hover:shadow-[0_0_50px_-6px_rgba(59,130,246,0.5),0_0_30px_-6px_rgba(59,130,246,0.8)]
          dark:hover:shadow-[0_0_50px_-6px_rgba(59,130,246,0.5),0_0_30px_-6px_rgba(59,130,246,0.8)]
        `}
				style={{
					transform: isHovered
						? `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`
						: "perspective(1000px)",
					boxShadow: isHovered
						? `
              ${position.x}px ${position.y}px 40px -10px rgba(0, 0, 0, 0.2),
              0 0 50px -6px rgba(59, 130, 246, 0.5),
              0 0 30px -6px rgba(59, 130, 246, 0.8)
            `
						: "none",
					transition: "all 150ms ease-out, box-shadow 150ms ease-out",
				}}
			>
				{/* Gradient overlay - different for light/dark modes */}
				<div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 rounded-lg">
					<div className="absolute inset-0 bg-gradient-to-br from-gray-100/50 via-white/30 to-gray-100/50 dark:from-blue-500/10 dark:via-purple-500/10 dark:to-pink-500/10 rounded-lg" />
				</div>

				{/* Images */}
				<img
					src={example1}
					alt="Dashboard Preview"
					className="w-full hidden dark:block transition-transform duration-150 group-hover:scale-[1.02] rounded-lg"
				/>
				<img
					src={example2}
					alt="Dashboard Preview"
					className="w-full block dark:hidden transition-transform duration-150 group-hover:scale-[1.02] rounded-lg"
				/>
			</div>
		</div>
	);
};

const Footer = () => (
	<footer className="mt-auto py-8 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
						About MyCron
					</h3>
					<p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
						Made to make your day simpler. Utilizing the latest AI models to
						make it easy to plan, manage, and unlock the full potential of your
						day.
					</p>
				</div>
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
						Contact
					</h3>
					<p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
						Built by{" "}
						<a
							href="mailto:rjbulcher@gmail.com"
							className="text-blue-600 dark:text-blue-400 hover:underline"
						>
							rjbulcher@gmail.com
						</a>
					</p>
				</div>
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
						Legal
					</h3>
					<ul className="mt-4 space-y-2">
						<li>
							<Link
								to="/privacy-policy"
								className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline"
							>
								Privacy Policy
							</Link>
						</li>
					</ul>
				</div>
			</div>
			<div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
				<p className="text-sm text-center text-gray-500 dark:text-gray-400">
					© {new Date().getFullYear()} MyCron. All rights reserved.
				</p>
			</div>
		</div>
	</footer>
);

const FeatureCard = ({ feature, index }) => {
	const [rotation, setRotation] = useState({ x: 0, y: 0 });
	const [isHovered, setIsHovered] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });

	const handleMouseMove = (e) => {
		if (!isHovered) return;

		const container = e.currentTarget;
		const rect = container.getBoundingClientRect();

		const x = (e.clientX - rect.left) / rect.width;
		const y = (e.clientY - rect.top) / rect.height;

		const rotateX = (0.5 - y) * 10;
		const rotateY = (x - 0.5) * 10;

		const shadowX = (0.5 - x) * 20;
		const shadowY = (0.5 - y) * 20;

		setRotation({ x: rotateX, y: rotateY });
		setPosition({ x: shadowX, y: shadowY });
	};

	const handleMouseLeave = () => {
		setIsHovered(false);
		setRotation({ x: 0, y: 0 });
		setPosition({ x: 0, y: 0 });
	};

	const handleMouseEnter = () => {
		setIsHovered(true);
	};

	return (
		<div
			onMouseMove={handleMouseMove}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			className="relative w-full group"
		>
			<div
				className={`
			relative w-full p-6
			transition-all duration-150 ease-out transform-gpu 
			rounded-lg
			bg-gray-50 dark:bg-gray-700
			hover:shadow-[0_0_50px_-6px_rgba(59,130,246,0.5),0_0_30px_-6px_rgba(59,130,246,0.8)]
			dark:hover:shadow-[0_0_50px_-6px_rgba(59,130,246,0.5),0_0_30px_-6px_rgba(59,130,246,0.8)]
			border border-gray-200/20 dark:border-gray-600/20
			backdrop-blur-sm
		  `}
				style={{
					transform: isHovered
						? `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`
						: "perspective(1000px)",
					boxShadow: isHovered
						? `
				${position.x}px ${position.y}px 40px -10px rgba(0, 0, 0, 0.2),
				0 0 50px -6px rgba(59, 130, 246, 0.5),
				0 0 30px -6px rgba(59, 130, 246, 0.8)
			  `
						: "none",
					transition: "all 150ms ease-out, box-shadow 150ms ease-out",
				}}
			>
				{/* Gradient overlay */}
				<div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 rounded-lg">
					<div className="absolute inset-0 bg-gradient-to-br from-gray-100/50 via-white/30 to-gray-100/50 dark:from-blue-500/10 dark:via-purple-500/10 dark:to-pink-500/10 rounded-lg" />
				</div>

				{/* Content */}
				<div className="relative">
					<div className="text-blue-600 dark:text-blue-400 mb-4 transition-transform duration-150 group-hover:scale-110">
						{feature.icon}
					</div>
					<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 transition-all duration-150 group-hover:translate-x-0.5">
						{feature.title}
					</h3>
					<p className="text-gray-500 dark:text-gray-400 transition-all duration-150 group-hover:translate-x-0.5">
						{feature.description}
					</p>
				</div>
			</div>
		</div>
	);
};

const LoginScreen = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { isAuthenticated, loading } = useAuth();

	useEffect(() => {
		if (isAuthenticated) {
			navigate("/dashboard", { replace: true });
		}
	}, [isAuthenticated, navigate]);

	if (loading) {
		return <LoadingScreen />;
	}

	const error = searchParams.get("error");

	const features = [
		{
			icon: <CalendarDays className="w-6 h-6" />,
			title: "Natural Language Input",
			description:
				"Ask something like: 'What's my day looking like tomorrow?'",
		},
		{
			icon: <Sparkles className="w-6 h-6" />,
			title: "AI Insights",
			description:
				"Recommendations to improve your productivity and time management",
		},
		{
			icon: <Clock className="w-6 h-6" />,
			title: "Time Optimization",
			description: "Intelligent time blocking and schedule optimization",
		},
		{
			icon: <Bot className="w-6 h-6" />,
			title: "AI Assistant",
			description: "Your personal AI calendar assistant available 24/7",
		},
		{
			icon: <Users className="w-6 h-6" />,
			title: "Team Collaboration",
			description:
				"Find the best time for your team to meet and coordinate schedules effortlessly",
		},
		{
			icon: <Lightbulb className="w-6 h-6" />,
			title: "Smarter Meetings",
			description:
				"Generate meeting invites with smart suggestions for time and location",
		},
	];

	const googleButtonClass = `
	  bg-transparent 
	  text-gray-700 dark:text-gray-200 
	  hover:bg-gray-100/50 dark:hover:bg-gray-700/50
	  border border-gray-300 dark:border-gray-600 
	  px-3 py-1.5 rounded-md
	  transition-all duration-150 ease-out
	  hover:shadow-[0_0_30px_-6px_rgba(59,130,246,0.5)]
	  dark:hover:shadow-[0_0_30px_-6px_rgba(59,130,246,0.5)]
	  min-w-[90px]
	`;

	return (
		<div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
			{/* Navigation */}
			<nav className="fixed w-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-50 border-b border-gray-200 dark:border-gray-700">
				<div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
					<div className="h-16 flex items-center justify-between gap-2">
						{/* Logo Container */}
						<div className="flex-shrink-0 flex items-center">
							<img
								src={darkLogo}
								alt="MyCron Logo"
								className="h-8 w-auto hidden dark:block"
							/>
							<img
								src={lightLogo}
								alt="MyCron Logo"
								className="h-8 w-auto block dark:hidden"
							/>
						</div>

						{/* Controls Container */}
						<div className="flex items-center gap-2 sm:gap-4">
							<div className="flex-shrink-0">
								<ThemeToggle />
							</div>
							<div className="flex-shrink-0">
								<GoogleAuthButton
									onError={(error) =>
										console.error("Google auth error:", error)
									}
									className={googleButtonClass}
									compact={true}
								/>
							</div>
						</div>
					</div>
				</div>
			</nav>

			{/* Main Content */}
			<main className="flex-grow">
				{/* Hero Section */}
				<div className="pt-24 pb-8 sm:pb-16 px-4 sm:px-6 lg:px-8">
					<div className="max-w-7xl mx-auto">
						<div className="lg:grid lg:grid-cols-12 lg:gap-8">
							<div className="text-center lg:text-left lg:col-span-6">
								<h1 className="text-4xl tracking-tight font-extrabold text-gray-900 dark:text-white sm:text-5xl md:text-6xl">
									<span className="block">Your AI-Powered</span>
									<span className="block text-blue-600 dark:text-blue-500">
										Calendar Assistant
									</span>
								</h1>
								<p className="mt-3 text-base text-gray-500 dark:text-gray-400 sm:mt-5 sm:text-lg space-y-4">
									<span className="block">
										Imagine having a personal assistant who knows your schedule
										inside and out, but better. MyCron brings your calendar to
										life with AI that truly understands how you work. Simply ask
										"What's my day looking like tomorrow?" or "Find me a 2-hour
										slot for focused work next week" and watch as it
										intelligently organizes your time.
									</span>
								</p>
								<div className="mt-8 flex justify-center lg:justify-start">
									<GoogleAuthButton
										onError={(error) =>
											console.error("Google auth error:", error)
										}
										className={googleButtonClass}
										compact={false}
									/>
								</div>
							</div>
							<div className="mt-8 sm:mt-12 lg:mt-0 lg:col-span-6">
								<div className="sm:px-6 lg:px-0">
									<AnimatedExampleImage />
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Features Section */}
				<div className="py-12 sm:py-16 bg-white dark:bg-gray-800">
					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
						<div className="grid md:grid-cols-3 gap-6 sm:gap-8">
							{features.map((feature, index) => (
								<FeatureCard key={index} feature={feature} index={index} />
							))}
						</div>
					</div>
				</div>
			</main>

			{/* Footer */}
			<Footer />

			{/* Error Alert */}
			{error && (
				<div className="fixed bottom-4 right-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md shadow-lg">
					<p className="text-sm text-red-600 dark:text-red-400">
						{error === "auth_failed"
							? "Authentication failed. Please try again."
							: error === "missing_data"
							? "Login data incomplete. Please try again."
							: "An error occurred. Please try again."}
					</p>
				</div>
			)}
		</div>
	);
};

export default LoginScreen;
